import { useEffect } from "react";
import { useTranslate } from "@refinedev/core";
import { Button, Flex, Form, Grid, Space, Spin } from "antd";
import useForm from "antd/es/form/hooks/useForm";
import { useSession } from "hooks/useSession";
import { useAuthActions } from "hooks/useAuthActions";
import styled from "styled-components";
import { useAntTheme } from "hooks/useAntTheme";
import PageHeader from "components/PageHeader";
import { BigInput } from "components/BigInput/BigInput/";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics/";
import {
  FileArrowUpIcon,
  SignpostIcon,
  ChartBarIcon,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { radius } from "@libertify/ui";

export default function CreateOrganizationPage() {
  const { user, isLoading, organizations } = useSession();
  const {
    createOrganization,
    isCreatingOrg: isCreating,
    setSidebarCollapsed,
  } = useAuthActions();
  const breakpoint = Grid.useBreakpoint();
  const navigate = useNavigate();

  const [form] = useForm();
  const { theme, isMobile } = useAntTheme();

  const t = useTranslate();
  const { trackOnboardingEvent } = useAnalytics();

  // Redirect users who already have organizations
  useEffect(() => {
    if (!isLoading && organizations.length > 0) {
      navigate("/home", { replace: true });
    }
  }, [isLoading, organizations, navigate]);

  useEffect(() => {
    setSidebarCollapsed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFinish = async (values: { name: string }) => {
    const organization = await createOrganization(values.name);

    if (organization?.data) {
      trackOnboardingEvent(AnalyticsEvents.OrganizationCreated, {
        is_first_organization: true,
        organization_id: organization.data.id,
      });
      navigate("/home");
    }
  };

  return (
    <div style={{ padding: isMobile ? 20 : 0 }}>
      <Spin fullscreen spinning={isLoading} />
      <Flex
        data-testid="organization-create"
        vertical
        justify="center"
        align="center"
        style={{ minHeight: `calc(100vh - ${theme.paddingLG * 2}px)` }}
        gap={theme.paddingLG}
      >
        <Flex
          vertical
          style={{
            backgroundColor: theme.colorPrimary,
            height: "auto",
            borderRadius: radius.box,
            padding: breakpoint.xxl ? 30 : 25,
          }}
        >
          <div
            style={{
              fontSize: breakpoint.xxl ? 60 : 50,
              color: theme.colorBgElevated,
            }}
          >
            {t("pages.home.index.hello")} {user?.first_name} 👋🏻
          </div>
        </Flex>
        <StyledForm
          form={form}
          onFinish={(values) => {
            handleFinish(values as { name: string }).catch((error) => {
              console.error(error);
            });
          }}
          style={{ marginTop: 30, width: "100%" }}
          layout="vertical"
        >
          <PageHeader
            title={t("pages.onboarding.createOrganization.title")}
            dot={false}
            subtitle={t("pages.onboarding.createOrganization.secondaryText")}
            subtitleStyles={{ color: theme.colorText }}
          />
          <Form.Item
            name="name"
            style={{ width: "100%" }}
            rules={[
              {
                required: true,
              },
              {
                pattern: /^[ -~]+$/,
                message: t(
                  "pages.onboarding.createOrganization.invalidCharacters"
                ),
              },
            ]}
          >
            <BigInput
              size="large"
              placeholder={t("media.organizations.index.organizationName")}
              style={{
                marginBottom: 10,
                width: isMobile ? "100%" : 400,
              }}
            />
          </Form.Item>
          <div style={{ fontStyle: "italic", color: "#888" }}>
            {t("media.organizations.index.companyExample")}
          </div>
          <Form.Item>
            <Button
              loading={isCreating}
              disabled={isLoading || isCreating}
              htmlType="submit"
              shape="round"
              size="large"
              type="primary"
              style={{
                marginTop: theme.paddingLG,
                margin: "20px auto",
              }}
              block={isMobile}
            >
              {t("pages.onboarding.buttons.continue")}
            </Button>
          </Form.Item>
          <Space split={<span>•</span>}>
            <StyledStepItem>{t("buttons.next")}</StyledStepItem>
            <StyledStepItem>
              <FileArrowUpIcon size={20} />
              {t("media.organizations.index.uploadDocument")}
            </StyledStepItem>
            <StyledStepItem>
              <SignpostIcon size={20} />
              {t("media.organizations.index.guideYourAudience")}
            </StyledStepItem>
            <StyledStepItem>
              <ChartBarIcon size={20} />
              {t("media.organizations.index.seeUnderstand")}
            </StyledStepItem>
          </Space>
        </StyledForm>
      </Flex>
    </div>
  );
}

const StyledForm = styled(Form)`
  text-align: center;
`;

const StyledStepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
  color: black;
  font-size: 18px;
`;
